/**
 * Feature Flag Definitions
 *
 * This file contains all feature flags used throughout the Soteria application.
 */

export const FEATURE_FLAGS = {
  NOTIFICATIONS: 'NOTIFICATIONS',
  COMMUNITY_GROUP_OBJECTS: 'COMMUNITY_GROUP_OBJECTS',
  CALLBACK_AUTHENTICATION: 'CALLBACK_AUTHENTICATION',
  SECURITY_GROUPS: 'SECURITY_GROUPS',
  AUTHENTICATION_PROVIDERS: 'AUTHENTICATION_PROVIDERS',
  USERNAME_CLASSIFICATION: 'USERNAME_CLASSIFICATION',
  CASE_SEARCH: 'CASE_SEARCH',
  ACTION_ADVANCED_SETTINGS: 'ACTION_ADVANCED_SETTINGS',
  SUBMIT_REPORT_ACTION: 'SUBMIT_REPORT_ACTION',
  SHOW_MODERATION_STATS_CARDS: 'SHOW_MODERATION_STATS_CARDS',
  ABR_FILTERING: 'ABR_FILTERING',
  V2_CARD_DESIGN: 'V2_CARD_DESIGN',
  AUDIT_POLICY_METRICS: 'AUDIT_POLICY_METRICS',
} as const;

export type FeatureFlag = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

/**
 * Feature Flag Display Names
 *
 * Maps feature flag keys to user-friendly display names for the UI.
 * If a feature flag is not defined here, the flag string will be used as fallback.
 */
export const FEATURE_FLAG_DISPLAY_NAMES: Record<FeatureFlag, string> = {
  [FEATURE_FLAGS.NOTIFICATIONS]: 'Notifications',
  [FEATURE_FLAGS.COMMUNITY_GROUP_OBJECTS]: 'Community Group Objects',
  [FEATURE_FLAGS.CALLBACK_AUTHENTICATION]: 'Callback Authentication',
  [FEATURE_FLAGS.SECURITY_GROUPS]: 'Security Groups',
  [FEATURE_FLAGS.AUTHENTICATION_PROVIDERS]: 'Authentication Providers',
  [FEATURE_FLAGS.USERNAME_CLASSIFICATION]: 'Username Classification',
  [FEATURE_FLAGS.CASE_SEARCH]: 'Case Search',
  [FEATURE_FLAGS.ACTION_ADVANCED_SETTINGS]: 'Action Advanced Settings (JSON Payload & Operator Name)',
  [FEATURE_FLAGS.SUBMIT_REPORT_ACTION]: 'Submit Report Action (Moderation Case Creation)',
  [FEATURE_FLAGS.SHOW_MODERATION_STATS_CARDS]: 'Show Moderation Stats Cards',
  [FEATURE_FLAGS.ABR_FILTERING]: 'ABR Queue Filtering',
  [FEATURE_FLAGS.V2_CARD_DESIGN]: 'V2 Card Design',
  [FEATURE_FLAGS.AUDIT_POLICY_METRICS]: 'Audit Policy Metrics Toggle',
} as const;

/**
 * Get the display name for a feature flag
 * @param featureFlag - The feature flag to get the display name for
 * @returns The display name if defined, otherwise the feature flag string
 */
export function getFeatureFlagDisplayName(featureFlag: FeatureFlag): string {
  return FEATURE_FLAG_DISPLAY_NAMES[featureFlag] || featureFlag;
}
